<template>
  <a-button type="outline" @click="handleClick">AI 生成题目</a-button>
  <a-drawer
    :visible="visible"
    :width="340"
    @cancel="handleCancel"
    @ok="handleOk"
    unmountOnClose
  >
    <template #title> AI 生成题目</template>
    <div>
      <a-form
        :model="form"
        auto-label-width
        label-align="left"
        @submit="handleSubmit"
      >
        <a-form-item label="应用 id">
          {{ appId }}
        </a-form-item>
        <a-form-item field="questionNumber" label="题目数量">
          <a-input-number
            v-model="form.questionNumber"
            :max="20"
            :min="0"
            placeholder="请输入题目数量"
          />
        </a-form-item>
        <a-form-item field="optionNumber" label="选项数量">
          <a-input-number
            v-model="form.optionNumber"
            :max="6"
            :min="0"
            placeholder="请输入选项数量"
          />
        </a-form-item>
        <a-form-item>
          <a-space style="margin-left: -40px" size="large">
            <a-button
              :loading="submitting"
              html-type="submit"
              style="width: 120px"
              type="primary"
            >
              {{ submitting ? "生成中" : "一键生成" }}
            </a-button>
            <a-button
              :loading="sseSubmitting"
              style="width: 120px"
              @click="handleSSESubmit"
            >
              {{ sseSubmitting ? "生成中" : "实时生成" }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </div>
  </a-drawer>
</template>

<script lang="ts" setup>
import { defineProps, reactive, ref, withDefaults } from "vue";
import API from "@/api";
import { aiGenerateQuestionUsingPost } from "@/api/questionController";
import message from "@arco-design/web-vue/es/message";
import { EventSourcePolyfill } from "event-source-polyfill";

interface Props {
  appId: string;
  onSuccess?: (result: API.QuestionContentDTO[]) => void;
  onSSESuccess?: (result: API.QuestionContentDTO) => void;
  onSSEStart?: (event: any) => void;
  onSSEClose?: (event: any) => void;
}

const props = withDefaults(defineProps<Props>(), {
  appId: () => {
    return "";
  },
});

const form = reactive({
  optionNumber: 2,
  questionNumber: 10,
} as API.AiGenerateQuestionRequest);

const visible = ref(false);
const submitting = ref(false);
const sseSubmitting = ref(false);

const handleClick = () => {
  visible.value = true;
};
const handleOk = () => {
  visible.value = false;
};
const handleCancel = () => {
  visible.value = false;
};

/**
 * 提交
 */
const handleSubmit = async () => {
  if (!props.appId) {
    return;
  }
  submitting.value = true;
  const res = await aiGenerateQuestionUsingPost({
    appId: props.appId as any,
    ...form,
  });
  if (res.data.code === 0 && res.data.data.length > 0) {
    if (props.onSuccess) {
      props.onSuccess(res.data.data);
    } else {
      message.success("生成题目成功");
    }
    // 关闭抽屉
    handleCancel();
  } else {
    message.error("操作失败，" + res.data.message);
  }
  submitting.value = false;
};

/**
 * 提交（实时生成）
 */
const handleSSESubmit = async () => {
  if (!props.appId) {
    return;
  }
  sseSubmitting.value = true;
  // 创建 SSE 请求
  const url = `http://localhost:8101/api/question/ai_generate/sse?appId=${props.appId}&optionNumber=${form.optionNumber}&questionNumber=${form.questionNumber}`;

  // 使用 Polyfill 替代原生 EventSource
  const eventSource = new EventSourcePolyfill(url, {
    withCredentials: true, // ✅ 携带 Cookie
    headers: {
      Accept: "text/event-stream",
    },
  });
  let first = true;
  // 添加关闭事件监听
  eventSource.addEventListener("close", function (event) {
    console.log("SSE 连接正常关闭");
    props.onSSEClose?.(event);
    eventSource.close();
    sseSubmitting.value = false;
    return;
  });
  // 接收消息
  eventSource.onmessage = function (event) {
    if (first) {
      props.onSSEStart?.(event);
      handleCancel();
      first = false;
    }
    try {
      const data = JSON.parse(event.data);
      props.onSSESuccess?.(data);
    } catch (e) {
      console.error("解析消息失败:", event.data);
    }
  };
  // 报错或连接关闭时触发
  eventSource.onerror = function (event: any) {
    const readyState = event.target.readyState;

    // 连接正常关闭（服务端完成推送）
    // if (readyState === EventSourcePolyfill.CLOSED) {
    //   console.log("连接正常关闭");
    //   props.onSSEClose?.(event);
    //   sseSubmitting.value = false;
    //   return;
    // }

    // 连接异常（正在重连）
    if (readyState === EventSourcePolyfill.CONNECTING) {
      console.log("连接异常，正在重连");
      message.error("生成过程出错，请重试");
      eventSource.close();
      sseSubmitting.value = false;
      return;
    }

    // 连接已打开但出错
    if (readyState === EventSourcePolyfill.OPEN) {
      console.log("连接已打开但出错");
      message.error("生成过程出错");
      eventSource.close();
      sseSubmitting.value = false;
      return;
    }

    // 其他未知错误
    console.error("未知连接错误:", event);
    eventSource.close();
    sseSubmitting.value = false;
  };
};
</script>
